import axios from 'axios';

const KEYCLOAK_URL = process.env.VUE_APP_KEYCLOAK_URL;
const REALM_NAME = process.env.VUE_APP_KEYCLOAK_REALM_NAME;
const CLIENT_ID = process.env.VUE_APP_KEYCLOAK_CLIENT_ID;
const CLIENT_SECRET = process.env.VUE_APP_KEYCLOAK_CLIENT_SECRET;
const REDIRECT_URI = process.env.VUE_APP_REDIRECT_URI;

const AUTH_URL = `${KEYCLOAK_URL}/realms/${REALM_NAME}/protocol/openid-connect/auth`;
const TOKEN_URL = `${KEYCLOAK_URL}/realms/${REALM_NAME}/protocol/openid-connect/token`;
const LOGOUT_URL = `${KEYCLOAK_URL}/realms/${REALM_NAME}/protocol/openid-connect/logout`;
const RESET_CREDENTIALS_URL = `${KEYCLOAK_URL}/realms/${REALM_NAME}/login-actions/reset-credentials`;

const TOKEN_KEY = 'kc_access_token';
const REFRESH_KEY = 'kc_refresh_token';
const ID_TOKEN_KEY = 'kc_id_token';
const CODE_VERIFIER_KEY = 'oauth_code_verifier';

/**
 * Interval at which the access_token is proactively refreshed to keep the Keycloak SSO
 * session alive while the browser tab stays open, even without further user interaction.
 * Defaults to 4 minutes, safely below Keycloak's default 5 minute access token lifespan.
 * Refreshing the token does not extend the session beyond Keycloak's own SSO Session Max
 * (currently configured as 12 hours), it only prevents the *idle* timeout from expiring it.
 */
const KEEP_ALIVE_INTERVAL_MS = Number(process.env.VUE_APP_KEYCLOAK_KEEPALIVE_INTERVAL_MS) || 4 * 60 * 1000;

let keepAliveTimer: ReturnType<typeof setInterval> | null = null;

const base64UrlEncode = (bytes: Uint8Array): string => {
  return btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const randomBytes = (length: number): Uint8Array => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return array;
};

// Generate random state string for CSRF protection
const generateState = (): string => base64UrlEncode(randomBytes(16));

/**
 * Build the PKCE code_challenge (S256) for a given code_verifier.
 * PKCE is required by the Keycloak clients on the production realm and is
 * harmless on realms where it is optional.
 */
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return base64UrlEncode(new Uint8Array(digest));
};

/**
 * Redirect user to Keycloak login page.
 * Uses Authorization Code flow with client_secret (Confidential client).
 * Email is prefilled using login_hint parameter.
 *
 * `action` optionally triggers an Application Initiated Action (AIA) via the
 * `kc_action` parameter (e.g. 'UPDATE_PASSWORD'), so Keycloak drops the user
 * straight into that hosted screen after authenticating.
 */
export const redirectToKeycloak = async (email: string, forceLogin = false, action?: string): Promise<void> => {
  const state = generateState();
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('login_hint_email', email);

  const codeVerifier = base64UrlEncode(randomBytes(32));
  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    redirect_uri: REDIRECT_URI!,
    response_type: 'code',
    scope: 'openid email profile',
    state,
    login_hint: email,
    code_challenge: await generateCodeChallenge(codeVerifier),
    code_challenge_method: 'S256',
    ...(forceLogin ? { prompt: 'login' } : {}),
    ...(action ? { kc_action: action } : {}),
  });

  window.location.href = `${AUTH_URL}?${params.toString()}`;
};

/**
 * Redirect user straight to Keycloak's own "Forgot password" (reset-credentials) page,
 * with the email prefilled via `login_hint`. Keycloak itself takes care of emailing the
 * user a link to set their password — the WS Portal never sends this email itself.
 */
export const redirectToKeycloakResetCredentials = (email: string): void => {
  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    login_hint: email,
  });

  window.location.href = `${RESET_CREDENTIALS_URL}?${params.toString()}`;
};

/**
 * Exchange authorization code for access_token and refresh_token.
 * Called in callback.vue after Keycloak redirects back.
 */
export const exchangeCodeForToken = async (code: string, state: string): Promise<boolean> => {
  const savedState = sessionStorage.getItem('oauth_state');

  if (state !== savedState) {
    return false;
  }

  sessionStorage.removeItem('oauth_state');

  const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID!,
    ...(CLIENT_SECRET ? { client_secret: CLIENT_SECRET } : {}),
    ...(codeVerifier ? { code_verifier: codeVerifier } : {}),
    redirect_uri: REDIRECT_URI!,
    code,
  });

  const response = await axios.post(TOKEN_URL, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  localStorage.setItem(TOKEN_KEY, response.data.access_token);
  localStorage.setItem(REFRESH_KEY, response.data.refresh_token);
  if (response.data.id_token) {
    localStorage.setItem(ID_TOKEN_KEY, response.data.id_token);
  }
  return true;
};

/**
 * Attempt to refresh access_token using refresh_token.
 * Returns new access_token or null if refresh is not possible.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) {
    return null;
  }

  try {
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID!,
      ...(CLIENT_SECRET ? { client_secret: CLIENT_SECRET } : {}),
      refresh_token: refresh,
    });

    const response = await axios.post(TOKEN_URL, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    localStorage.setItem(TOKEN_KEY, response.data.access_token);
    localStorage.setItem(REFRESH_KEY, response.data.refresh_token);
    return response.data.access_token;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    return null;
  }
};

export const getStoredToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const clearTokens = (): void => {
  stopKeepAlive();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
};

/**
 * Start the keep-alive mechanism: periodically refresh the access_token (using the
 * refresh_token) so the underlying Keycloak SSO session doesn't expire due to inactivity
 * while the tab stays open. Safe to call multiple times, only one timer is ever active.
 *
 * If a refresh ever fails (eg. the Keycloak SSO session itself expired/was revoked,
 * reaching the SSO Session Max of 12 hours), the timer stops itself.
 */
export const startKeepAlive = (): void => {
  if (keepAliveTimer || !getStoredToken()) {
    return;
  }

  keepAliveTimer = setInterval(async () => {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      stopKeepAlive();
    }
  }, KEEP_ALIVE_INTERVAL_MS);
};

export const stopKeepAlive = (): void => {
  if (keepAliveTimer) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
};

/**
 * Redirect to Keycloak end_session endpoint to fully invalidate the SSO session.
 * After Keycloak logout, the user is redirected back to `postLogoutRedirectUri`.
 *
 * Note: id_token_hint is intentionally NOT sent — Keycloak uses it as a CSRF
 * token to silently skip its own logout confirmation screen. By omitting it
 * (and only sending client_id + post_logout_redirect_uri), Keycloak shows the
 * user a confirmation page and only redirects back after they explicitly
 * confirm the logout.
 *
 * Local tokens/auth state are intentionally NOT cleared here. If the user
 * cancels the logout on Keycloak's confirmation screen, Keycloak never
 * redirects back, so the WS Portal session must remain intact. Local state
 * is only cleared in the logout callback route, which is reached exclusively
 * after Keycloak confirms the logout.
 */
export const logoutFromKeycloak = (postLogoutRedirectUri: string): void => {
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri,
    client_id: CLIENT_ID!,
  });

  window.location.href = `${LOGOUT_URL}?${params.toString()}`;
};
